import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { CardShell, FieldLabel } from "./card-handler";
import { TemplateModule } from "../../../../views/marketing-pages/templates";

// Attribute options for the Email condition path — email/call/lead
// engagement fields that already existed.
const EMAIL_ATTRIBUTE_OPTIONS = [
  { label: "Email Engagement", value: "email_engaged" },
  { label: "Email Event Type", value: "email_event_type" },
  { label: "Reply Received", value: "reply_received" },
  { label: "Call Engaged", value: "call_engaged" },
  { label: "Call Outcome", value: "call_outcome" },
  { label: "Lead Status", value: "lead_status" },
  { label: "Lead Score", value: "lead_score" },
  { label: "Source", value: "source" },
  { label: "First Name", value: "first_name" },
  { label: "Tags", value: "tags" },
  { label: "Custom Attribute", value: "__custom__" },
];

// Attribute options for the WhatsApp condition path — the two
// checkboxes the VouchLinks share form ticks, plus a custom escape
// hatch. Nothing email-signal-related here (Email Engagement / Event
// Type / etc.) because the workflow's signal-wait plumbing only fires
// on email events.
const WHATSAPP_ATTRIBUTE_OPTIONS = [
  { label: "Rate Checkbox Ticked",   value: "rate" },
  { label: "Signup Checkbox Ticked", value: "signup" },
  { label: "Lead Status",            value: "lead_status" },
  { label: "First Name",             value: "first_name" },
  { label: "Source",                 value: "source" },
  { label: "Custom Attribute",       value: "__custom__" },
];

const CHANNEL_OPTIONS = [
  { label: "Email",    value: "Email" },
  { label: "WhatsApp", value: "WhatsApp" },
];

// Backend signal-backed fields — the condition card auto-waits up to wait_timeout_seconds
// for the matching signal to arrive before evaluating (commit 9b27caa).
const SIGNAL_BACKED_FIELDS = new Set([
  "email_engaged",
  "email_event_type",
  "reply_received",
  "reply_text",
  "call_engaged",
  "call_outcome",
]);

const CONDITION_OPERATORS = [
  { label: "Equals", value: "equals" },
  { label: "Not Equals", value: "not_equals" },
  { label: "Contains", value: "contains" },
  { label: "Does Not Contain", value: "not_contains" },
  { label: "Starts With", value: "starts_with" },
  { label: "Ends With", value: "ends_with" },
  { label: "Greater Than", value: "greater_than" },
  { label: "Greater Than or Equal", value: "greater_than_or_equal" },
  { label: "Less Than", value: "less_than" },
  { label: "Less Than or Equal", value: "less_than_or_equal" },
  { label: "Exists", value: "exists" },
  { label: "Does Not Exist", value: "not_exists" },
];

const WAIT_UNIT_OPTIONS = [
   { label: "Seconds", value: "seconds", multiplier: 1 },
  { label: "Minutes", value: "minutes", multiplier: 60 },
  { label: "Hours", value: "hours", multiplier: 3600 },
  { label: "Days", value: "days", multiplier: 86400 },
];

interface ConditionConfig {
  channel?: "Email" | "WhatsApp";  // Drives which attribute set + UI to show
  customerAttribute?: string;      // Single-attr (backwards compat / one-attr conditions)
  customerAttributes?: string[];   // Multi-attr — operator picks 2+ (e.g. rate + signup)
  match_mode?: "all" | "any";      // How to combine multi-attr: all=AND, any=OR
  condition?: string;
  value?: string | number;
  waitTimeoutValue?: number;
  waitTimeoutUnit?: string;
  wait_timeout_seconds?: number;
  true_next?: string;
  false_next?: string;
  // WhatsApp path only — the condition self-hosts a "wait before sending"
  // input so the operator doesn't have to drop a separate Set Delay Time
  // node between the condition and the send. Backend reads delay_seconds
  // and sleeps after evaluating true → before advancing to true_next.
  // Zero / unset = fire immediately.
  delay_seconds?: number;
  delayValue?: number;
  delayUnit?: string;
  // WhatsApp multi-template mode — per picked attribute, the operator
  // pairs a template + wait. Backend iterates on match: for each entry,
  // if _variables[attribute] is truthy, sleep delay_seconds then fire
  // that template. Rate immediate + Signup 2h-later ships as two rows.
  whatsapp_actions?: WhatsAppActionRow[];
}

interface WhatsAppActionRow {
  attribute: string;    // e.g. "rate", "signup" — the lead custom.* flag
  template: string;     // WhatsApp template name to fire when attribute is true
  delay_seconds?: number;
  delayValue?: number;
  delayUnit?: string;
}

const DELAY_UNIT_OPTIONS = [
  { label: "Minutes", value: "minutes", multiplier: 60    },
  { label: "Hours",   value: "hours",   multiplier: 3600  },
  { label: "Days",    value: "days",    multiplier: 86400 },
];

interface ConditionCardProps {
  config: ConditionConfig;
  templates?: TemplateModule[];
  onUpdate: (config: ConditionConfig) => void;
  onDelete: () => void;
  onReset: () => void;
  bare?: boolean;
}

export const ConditionCard: React.FC<ConditionCardProps> = ({
  config,
  templates = [],
  onUpdate,
  onDelete,
  onReset,
  bare,
}) => {
  // Templates for the per-attribute WhatsApp send picker. Same filter as
  // send-email-card.tsx — approved WhatsApp templates only.
  const waTemplates = templates.filter((t) => {
    const ch = (t.channel || "").toLowerCase();
    return ch === "whatsapp" && (t.wa_status ?? "").toUpperCase() === "APPROVED";
  });
  const [attrOpen, setAttrOpen] = useState(false);
  const [opOpen, setOpOpen] = useState(false);
  const [valueOpen, setValueOpen] = useState(false);
  const [waitUnitOpen, setWaitUnitOpen] = useState(false);

  const attrRef = useRef<HTMLDivElement>(null);
  const opRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLDivElement>(null);
  const waitUnitRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (attrRef.current && !attrRef.current.contains(e.target as Node)) setAttrOpen(false);
      if (opRef.current && !opRef.current.contains(e.target as Node)) setOpOpen(false);
      if (valueRef.current && !valueRef.current.contains(e.target as Node)) setValueOpen(false);
      if (waitUnitRef.current && !waitUnitRef.current.contains(e.target as Node)) setWaitUnitOpen(false);
      if (channelRef.current && !channelRef.current.contains(e.target as Node)) setChannelOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const update = (patch: Partial<ConditionConfig>) => onUpdate({ ...config, ...patch });

  // Channel drives which attribute set the dropdown shows + whether the
  // wait-timeout row renders. Default is Email so existing conditions
  // (built before channel was a concept) keep the pre-refactor UX.
  const channel: "Email" | "WhatsApp" = config.channel ?? "Email";
  const attributeOptions =
    channel === "WhatsApp" ? WHATSAPP_ATTRIBUTE_OPTIONS : EMAIL_ATTRIBUTE_OPTIONS;
  const [channelOpen, setChannelOpen] = useState(false);
  const channelRef = useRef<HTMLDivElement>(null);

  // A "custom attribute" is any customerAttribute value NOT in the fixed
  // options list. We treat the "__custom__" sentinel as edit-mode; when
  // the user types a name we save it directly as customerAttribute so the
  // backend condition handler reads `_variables[<name>]` at eval time.
  const isCustomAttr =
    !!config.customerAttribute &&
    !attributeOptions.some(a => a.value === config.customerAttribute && a.value !== "__custom__");
  const selectedAttr = isCustomAttr
    ? { label: `Custom: ${config.customerAttribute}`, value: config.customerAttribute! }
    : attributeOptions.find(a => a.value === config.customerAttribute);
  const selectedOp = CONDITION_OPERATORS.find(o => o.value === config.condition);

  // Multi-attr state. If the config already has customerAttributes (new
  // shape) we use that verbatim. Otherwise we mirror the legacy single
  // customerAttribute into a one-item array so the multi-select renders
  // consistently — every change writes both fields so downstream code
  // that still reads customerAttribute keeps working.
  // Raw picked-attr list from config (single-attr legacy + multi-attr).
  const rawPickedAttrs: string[] =
    config.customerAttributes && config.customerAttributes.length > 0
      ? config.customerAttributes
      : config.customerAttribute
      ? [config.customerAttribute]
      : [];
  // Filter out attrs that only belong to the OTHER channel — otherwise
  // switching Email → WhatsApp on a pre-existing condition renders
  // spurious rows for "call_outcome" / "email_engaged" etc. that the
  // WhatsApp option set doesn't include. Custom names (typed by the
  // operator, not in either preset list) always pass through so the
  // "Custom Attribute" escape hatch keeps working.
  const otherChannelOnlyValues = new Set<string>(
    channel === "WhatsApp"
      ? EMAIL_ATTRIBUTE_OPTIONS
          .map(o => o.value)
          .filter(v => !WHATSAPP_ATTRIBUTE_OPTIONS.some(w => w.value === v))
      : WHATSAPP_ATTRIBUTE_OPTIONS
          .map(o => o.value)
          .filter(v => !EMAIL_ATTRIBUTE_OPTIONS.some(e => e.value === v))
  );
  const pickedAttrs: string[] = rawPickedAttrs.filter(a => !otherChannelOnlyValues.has(a));
  const matchMode: "all" | "any" = config.match_mode ?? "all";
  const toggleAttr = (attrValue: string) => {
    const next = pickedAttrs.includes(attrValue)
      ? pickedAttrs.filter(v => v !== attrValue)
      : [...pickedAttrs, attrValue];
    update({
      customerAttributes: next,
      customerAttribute: next[0],  // legacy mirror — first pick stays here
      value: undefined,
    });
  };

  // Per-attribute WhatsApp actions — one row per picked attribute,
  // each pairing a template with its own wait. Auto-synced so that
  // adding/removing an attribute in the multi-select preserves the
  // rows the operator has already configured for the other attrs.
  const inferDelayUnit = (s: number): string => {
    if (s <= 0) return "hours";
    if (s % 86400 === 0) return "days";
    if (s % 3600 === 0)  return "hours";
    return "minutes";
  };
  const existingActions: WhatsAppActionRow[] = config.whatsapp_actions ?? [];

  // Auto-template guesser — when the operator picks an attribute, we
  // try to find a template whose name mentions that attribute so they
  // don't have to hunt through the dropdown. Substring match against
  // template_name so it handles "vouchlinks_rate_reminder" for
  // attribute "rate", "vouchlinks_join_community" for "signup" (via
  // an alias table below), etc. Operator can still override via the
  // template picker on the row.
  const AUTO_TEMPLATE_ALIASES: Record<string, string[]> = {
    // "recommendation_feedback" is MJ's rate template; keep older
    // aliases so any tenant that made their own rate template still
    // auto-picks.
    rate:   ["recommendation_feedback", "feedback", "rate", "review"],
    // "recommendation_earnings" is MJ's join/community template.
    signup: ["recommendation_earnings", "earnings", "signup", "sign_up", "community", "join"],
  };
  const guessTemplate = (attr: string): string => {
    const aliases = AUTO_TEMPLATE_ALIASES[attr.toLowerCase()] ?? [attr.toLowerCase()];
    // Iterate aliases in priority order — the first alias that
    // matches ANY approved template wins. So "recommendation_feedback"
    // (MJ's canonical) beats "rate" (which would also match older
    // vouchlinks_rate_reminder_v2).
    for (const a of aliases) {
      const hit = waTemplates.find(t => (t.template_name || "").toLowerCase().includes(a));
      if (hit) return hit.template_name;
    }
    return "";
  };

  const waActionsUnsorted: WhatsAppActionRow[] = pickedAttrs.map(attr => {
    const prev = existingActions.find(a => a.attribute === attr);
    if (prev) {
      // Backfill the template if it's still empty on an existing row —
      // covers the case where templates hadn't loaded yet the first
      // time the operator ticked the attribute, so the auto-guess ran
      // against an empty list. Guess is a no-op when there's already
      // an operator-set template.
      if (!prev.template) {
        const guess = guessTemplate(attr);
        if (guess) return { ...prev, template: guess };
      }
      return prev;
    }
    return {
      attribute: attr,
      template:  guessTemplate(attr),
      delay_seconds: 0,
      delayValue: 0,
      delayUnit: "hours",
    };
  });
  // Sort rows by wait so the immediate one always renders first — the
  // top row visually aligns with the canvas "Send" arrow, the bottom
  // row with "Send after". Operators kept losing which row is which
  // when pick order (rate/signup) didn't match delay order (0h/2h).
  // (Joseph 2026-08-04 "i chose rate reminder for 0 hours and its in
  // send after check the logic")
  const waActions: WhatsAppActionRow[] = [...waActionsUnsorted].sort(
    (a, b) => (Number(a.delay_seconds) || 0) - (Number(b.delay_seconds) || 0)
  );

  const updateAction = (attr: string, patch: Partial<WhatsAppActionRow>) => {
    const next = waActions.map(a => (a.attribute === attr ? { ...a, ...patch } : a));
    update({ whatsapp_actions: next });
  };
  const setActionDelay = (attr: string, value: string | number, unitValue?: string) => {
    const row = waActions.find(a => a.attribute === attr);
    const unit = DELAY_UNIT_OPTIONS.find(
      u => u.value === (unitValue ?? row?.delayUnit ?? inferDelayUnit(row?.delay_seconds ?? 0))
    ) ?? DELAY_UNIT_OPTIONS[1];
    const nextValue = Math.max(0, Number(value) || 0);
    updateAction(attr, {
      delayValue: nextValue,
      delayUnit: unit.value,
      delay_seconds: nextValue * unit.multiplier,
    });
  };
  const [openDelayUnitFor, setOpenDelayUnitFor] = useState<string | null>(null);
  const [openTemplateFor, setOpenTemplateFor] = useState<string | null>(null);

  // Wait-timeout row is only meaningful on email/call signal-backed
  // fields. Hide it entirely on the WhatsApp path — those conditions
  // read plain boolean flags off custom.rate / custom.signup that are
  // already resolved by the time the workflow evaluates.
  const shouldShowWaitTimeout =
    channel === "Email" && SIGNAL_BACKED_FIELDS.has(config.customerAttribute ?? "");

  // Pick the largest unit where wait_timeout_seconds divides evenly — so
  // 120s loads as "2 Minutes" (not "1 Hour" rounded down from 0.033), and
  // 7200s loads as "2 Hours" (not "0 Days").
  // Real users don't open emails within seconds — default = 2 days when fresh.
  const inferUnitFromSeconds = (secs: number): string => {
    if (secs <= 0) return "days";
    if (secs % 86400 === 0) return "days";
    if (secs % 3600 === 0) return "hours";
    if (secs % 60 === 0) return "minutes";
    return "seconds";
  };

  const fallbackUnit = config.wait_timeout_seconds
    ? inferUnitFromSeconds(config.wait_timeout_seconds)
    : "days";
  const selectedWaitUnit =
    WAIT_UNIT_OPTIONS.find((unit) => unit.value === (config.waitTimeoutUnit ?? fallbackUnit)) ??
    WAIT_UNIT_OPTIONS[3];

  const waitTimeoutValue = config.waitTimeoutValue ??
    (config.wait_timeout_seconds
      ? Math.max(1, Math.round(config.wait_timeout_seconds / selectedWaitUnit.multiplier))
      : 2); // ← Default: 2 days

  const updateWaitTimeout = (value: string | number, unitValue = selectedWaitUnit.value) => {
    const nextValue = Number(value) || 0;
    const nextUnit = WAIT_UNIT_OPTIONS.find((unit) => unit.value === unitValue) ?? selectedWaitUnit;

    update({
      waitTimeoutValue: nextValue,
      waitTimeoutUnit: nextUnit.value,
      wait_timeout_seconds: nextValue > 0 ? nextValue * nextUnit.multiplier : 0,
    });
  };

  const getValueOptions = () => {
    switch (config.customerAttribute) {
      case "email_engaged":
      case "reply_received":
      case "call_engaged":
      case "rate":     // VouchLinks checkbox — matches custom.rate boolean
      case "signup":   // VouchLinks checkbox — matches custom.signup boolean
        return [
          { label: "True", value: "true" },
          { label: "False", value: "false" },
        ];
      case "email_event_type":
        return [
          { label: "Opened", value: "opened" },
          { label: "Clicked", value: "clicked" },
          { label: "Delivered", value: "delivered" },
          { label: "Bounced", value: "bounced" },
          { label: "Rejected", value: "rejected" },
        ];
      case "call_outcome":
        return [
          { label: "Answered", value: "answered" },
          { label: "Callback", value: "callback" },
          { label: "No Answer", value: "no_answer" },
          { label: "Voicemail", value: "voicemail" },
        ];
      case "lead_status":
        return [
          { label: "New", value: "new" },
          { label: "Warm", value: "warm" },
          { label: "Hot", value: "hot" },
          { label: "Cold", value: "cold" },
          { label: "Qualified", value: "qualified" },
        ];
      default:
        return [];
    }
  };

  const valueOptions = getValueOptions();
  const isDropdownValue = valueOptions.length > 0;
  const isScore = config.customerAttribute === "lead_score";

  const getTrueLabel = () => {
    if (config.customerAttribute === "lead_status" && config.value) {
      return String(config.value).charAt(0).toUpperCase() + String(config.value).slice(1);
    }
    return "True";
  };

  const getFalseLabel = () => {
    if (config.customerAttribute === "lead_status" && config.value) {
      return "Other Status";
    }
    return "False";
  };

  return (
    <CardShell title="Condition" onDelete={onDelete} onReset={onReset} bare={bare}>
        {/* Channel — drives the attribute options below. Switching also
            clears customerAttribute + value so the operator can't accidentally
            carry an Email-only attribute (e.g. Email Event Type) onto a
            WhatsApp condition. */}
        <FieldLabel>Channel</FieldLabel>
        <div className="relative mb-3" ref={channelRef}>
          <button
            onClick={() => setChannelOpen(!channelOpen)}
            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm text-left flex items-center justify-between bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
          >
            <span className="text-gray-700 dark:text-gray-300">
              {CHANNEL_OPTIONS.find(c => c.value === channel)?.label}
            </span>
            <ChevronDown size={16} className={`transition-transform ${channelOpen ? "rotate-180" : ""}`} />
          </button>
          {channelOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
              {CHANNEL_OPTIONS.map((c) => (
                <button
                  key={c.value}
                  onClick={() => {
                    // Also clear customerAttributes + whatsapp_actions so
                    // attributes from the OTHER channel (e.g. "call_outcome"
                    // from Email) don't leak into the WhatsApp attribute
                    // list and spawn a spurious per-attribute row.
                    // (Joseph 2026-08-04 "remove this: If Custom:
                    // call_outcome is set → send:")
                    update({
                      channel: c.value as "Email" | "WhatsApp",
                      customerAttribute: undefined,
                      customerAttributes: [],
                      whatsapp_actions: [],
                      value: undefined,
                    });
                    setChannelOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 ${channel === c.value ? "bg-indigo-50 text-indigo-600" : ""}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Customer Attributes — multi-select (checkboxes). Legacy
            single-attr conditions still render (we mirror
            customerAttribute into customerAttributes on any change),
            but new conditions pick 2+ attributes and pair them with a
            Match Mode (all / any) that the backend collapses into AND
            or OR before evaluating. */}
        <FieldLabel>Customer Attributes</FieldLabel>
        <div className="relative mb-3" ref={attrRef}>
          <button
            onClick={() => setAttrOpen(!attrOpen)}
            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm text-left flex items-center justify-between bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
          >
            <span className={pickedAttrs.length ? "text-gray-700 dark:text-gray-300" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}>
              {pickedAttrs.length === 0
                ? "Select one or more attributes"
                : pickedAttrs
                    .map(v => attributeOptions.find(o => o.value === v)?.label ?? `Custom: ${v}`)
                    .join(", ")}
            </span>
            <ChevronDown size={16} className={`transition-transform ${attrOpen ? "rotate-180" : ""}`} />
          </button>

          {attrOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-72 overflow-auto">
              {attributeOptions.map((opt) => {
                const isPicked = pickedAttrs.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleAttr(opt.value)}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 flex items-center gap-2 ${isPicked ? "bg-indigo-50 text-indigo-600" : ""}`}
                  >
                    <span className={`inline-block w-4 h-4 border rounded ${isPicked ? "bg-indigo-600 border-indigo-600" : "border-gray-300 dark:border-gray-600"} flex items-center justify-center text-white text-[10px]`}>
                      {isPicked ? "✓" : ""}
                    </span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Match Mode — only meaningful when 2+ attrs picked AND on the
            Email path (WhatsApp uses per-row implicit "is truthy" checks
            in whatsapp_actions so match_mode is redundant there). */}
        {channel === "Email" && pickedAttrs.length >= 2 && (
          <div className="mb-3">
            <FieldLabel>Match Mode</FieldLabel>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => update({ match_mode: "all" })}
                className={`border rounded-lg px-3 py-2 text-sm ${matchMode === "all" ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"}`}
              >
                All (AND)
              </button>
              <button
                onClick={() => update({ match_mode: "any" })}
                className={`border rounded-lg px-3 py-2 text-sm ${matchMode === "any" ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"}`}
              >
                Any (OR)
              </button>
            </div>
            <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
              {matchMode === "all"
                ? "All picked attributes must match the value below."
                : "Any one of the picked attributes matching is enough."}
            </p>
          </div>
        )}

        {/* Operator + Value — Email path only. WhatsApp conditions read
            each picked attribute as an implicit "is True" (whatsapp_actions
            fires the paired template if the attribute is truthy on the
            lead's custom.*), so a shared operator+value picker would be
            confusing and redundant. */}
        {channel === "Email" && (
          <>
        <FieldLabel>Select Condition</FieldLabel>
        <div className="relative mb-3" ref={opRef}>
          <button
            onClick={() => setOpOpen(!opOpen)}
            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm text-left flex items-center justify-between bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
          >
            <span className={selectedOp ? "text-gray-700 dark:text-gray-300" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}>
              {selectedOp?.label || "Select operator"}
            </span>
            <ChevronDown size={16} className={`transition-transform ${opOpen ? "rotate-180" : ""}`} />
          </button>

          {opOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-60 overflow-auto">
              {CONDITION_OPERATORS.map((op) => (
                <button
                  key={op.value}
                  onClick={() => {
                    update({ condition: op.value });
                    setOpOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 ${config.condition === op.value ? "bg-indigo-50 text-indigo-600" : ""}`}
                >
                  {op.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Value */}
        <FieldLabel>Value</FieldLabel>
        {isDropdownValue ? (
          <div className="relative" ref={valueRef}>
            <button
              onClick={() => setValueOpen(!valueOpen)}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm text-left flex items-center justify-between bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              <span className="text-gray-700 dark:text-gray-300">
                {valueOptions.find(opt => opt.value === config.value)?.label || "Select value"}
              </span>
              <ChevronDown size={16} className={`transition-transform ${valueOpen ? "rotate-180" : ""}`} />
            </button>

            {valueOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
                {valueOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      update({ value: opt.value });
                      setValueOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 ${config.value === opt.value ? "bg-indigo-50 text-indigo-600" : ""}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : isScore ? (
          <input
            type="number"
            value={config.value ?? ""}
            onChange={(e) => update({ value: e.target.value })}
            placeholder="Enter score (e.g. 50)"
            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-300"
          />
        ) : (
          <input
            type="text"
            value={config.value ?? ""}
            onChange={(e) => update({ value: e.target.value })}
            placeholder="Enter value"
            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-300"
          />
        )}
          </>
        )}

        {/* Wait Timeout - Default 50 Seconds */}
        {shouldShowWaitTimeout && (
          <div className="mt-4">
            <FieldLabel>Wait up to</FieldLabel>
            <div className="grid grid-cols-[1fr_120px] gap-2">
              <input
                type="number"
                min={1}
                value={waitTimeoutValue}
                onChange={(e) => updateWaitTimeout(e.target.value)}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-300"
              />
              <div className="relative" ref={waitUnitRef}>
                <button
                  type="button"
                  onClick={() => setWaitUnitOpen(!waitUnitOpen)}
                  className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm text-left flex items-center justify-between bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                >
                  <span className="text-gray-700 dark:text-gray-300">{selectedWaitUnit.label}</span>
                  <ChevronDown size={16} className={`transition-transform ${waitUnitOpen ? "rotate-180" : ""}`} />
                </button>

                {waitUnitOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
                    {WAIT_UNIT_OPTIONS.map((unit) => (
                      <button
                        key={unit.value}
                        type="button"
                        onClick={() => {
                          updateWaitTimeout(waitTimeoutValue, unit.value);
                          setWaitUnitOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2.5 text-sm hover:bg-indigo-50 ${selectedWaitUnit.value === unit.value ? "bg-indigo-50 text-indigo-600" : ""}`}
                      >
                        {unit.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs mt-2 text-gray-500 dark:text-gray-400">
              Please wait for email engagements to avoid disruption from the server
            </p>
          </div>
        )}

        {/* Per-attribute template + wait — WhatsApp path only. One row
            for each attribute the operator picked above. Each row
            answers "when this checkbox is ticked, send THIS template
            after THIS wait" — so rate can go 0h and signup can go 2h
            on the same condition card. Backend iterates
            config.whatsapp_actions on match. */}
        {channel === "WhatsApp" && waActions.length > 0 && (
          <div className="mt-3 space-y-3">
            <FieldLabel>What to send for each ticked checkbox</FieldLabel>
            {waActions.map((row) => {
              const attrLabel =
                attributeOptions.find(o => o.value === row.attribute)?.label
                ?? `Custom: ${row.attribute}`;
              const rowUnit =
                DELAY_UNIT_OPTIONS.find(u => u.value === (row.delayUnit ?? inferDelayUnit(row.delay_seconds ?? 0)))
                ?? DELAY_UNIT_OPTIONS[1];
              const rowValue =
                typeof row.delayValue === "number"
                  ? row.delayValue
                  : ((row.delay_seconds ?? 0) > 0
                      ? Math.max(1, Math.round((row.delay_seconds ?? 0) / rowUnit.multiplier))
                      : 0);
              const selectedTpl = waTemplates.find(t => t.template_name === row.template);
              // "Rate Checkbox Ticked" / "Signup Checkbox Ticked" already
              // say the state — appending "is True" was redundant. Drop the
              // "Ticked" suffix for a cleaner row header. For unrecognised
              // attributes we still show "is ticked" so the user sees the
              // implicit condition.
              const isCheckboxAttr = /Checkbox Ticked$/i.test(attrLabel);
              const cleanAttrLabel = isCheckboxAttr
                ? attrLabel.replace(/\s*Checkbox\s*Ticked$/i, "")
                : attrLabel;
              return (
                <div key={row.attribute} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
                  <p className="text-xs font-medium text-indigo-700 mb-2">
                    If <span className="font-semibold">{cleanAttrLabel}</span>
                    {isCheckboxAttr ? " is ticked" : " is set"} → send:
                  </p>

                  <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">WhatsApp Template</label>
                  <div className="relative mb-3">
                    <button
                      type="button"
                      onClick={() => setOpenTemplateFor(openTemplateFor === row.attribute ? null : row.attribute)}
                      className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-left flex items-center justify-between bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                    >
                      <span className={selectedTpl ? "text-gray-700 dark:text-gray-300" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}>
                        {selectedTpl?.template_name ?? "Pick a WhatsApp template"}
                      </span>
                      <ChevronDown size={14} className={`transition-transform ${openTemplateFor === row.attribute ? "rotate-180" : ""}`} />
                    </button>
                    {openTemplateFor === row.attribute && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-auto">
                        {waTemplates.length === 0 && (
                          <p className="px-3 py-2 text-xs text-amber-600">
                            No approved WhatsApp templates for this tenant.
                          </p>
                        )}
                        {waTemplates.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => {
                              updateAction(row.attribute, { template: t.template_name });
                              setOpenTemplateFor(null);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 ${row.template === t.template_name ? "bg-indigo-50 text-indigo-600" : ""}`}
                          >
                            {t.template_name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Send after</label>
                  <div className="grid grid-cols-[1fr_110px] gap-2">
                    <input
                      type="number"
                      min={0}
                      value={rowValue}
                      onChange={(e) => setActionDelay(row.attribute, e.target.value)}
                      className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-300"
                    />
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setOpenDelayUnitFor(openDelayUnitFor === row.attribute ? null : row.attribute)}
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-left flex items-center justify-between bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                      >
                        <span className="text-gray-700 dark:text-gray-300">{rowUnit.label}</span>
                        <ChevronDown size={14} className={`transition-transform ${openDelayUnitFor === row.attribute ? "rotate-180" : ""}`} />
                      </button>
                      {openDelayUnitFor === row.attribute && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
                          {DELAY_UNIT_OPTIONS.map((unit) => (
                            <button
                              key={unit.value}
                              type="button"
                              onClick={() => { setActionDelay(row.attribute, rowValue, unit.value); setOpenDelayUnitFor(null); }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 ${rowUnit.value === unit.value ? "bg-indigo-50 text-indigo-600" : ""}`}
                            >
                              {unit.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] mt-1 text-gray-500 dark:text-gray-400">
                    {rowValue > 0
                      ? `Waits ${rowValue} ${rowUnit.label.toLowerCase()} after the lead is created before firing.`
                      : "Fires as soon as the lead is created."}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {/* Branch indicators — Email keeps the True/False pair (its flows
            typically use both). WhatsApp collapses to a single "When
            matched → next" pill because the VouchLinks-style pattern
            doesn't need a False branch (unmatched means don't send). */}
        {channel === "WhatsApp" ? (
          <div className="mt-4">
            <div className="flex items-center justify-between bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 text-sm">
              <span className="font-medium text-indigo-700">When matched → next step</span>
              <div className="w-3 h-3 rounded-full bg-indigo-500 border-2 border-white shadow" />
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-2">
            <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">{getTrueLabel()}</span>
              <div className="w-3 h-3 rounded-full bg-gray-400 border-2 border-white shadow" />
            </div>
            <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">{getFalseLabel()}</span>
              <div className="w-3 h-3 rounded-full bg-gray-400 border-2 border-white shadow" />
            </div>
          </div>
        )}
    </CardShell>
  );
};